import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Storage as StorageIcon,
  VpnKey as KeyIcon,
  Security as SecurityIcon,
  HelpOutlined as HelpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { routes } from "@/constants/routes";

export default function Help() {
  const { t } = useTranslation();
  const { keyCount } = useLocalKeys();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordion =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const localStorageAvailable = (() => {
    try {
      localStorage.setItem("_test", "1");
      localStorage.removeItem("_test");
      return true;
    } catch {
      return false;
    }
  })();

  const cryptoAvailable = typeof crypto?.subtle !== "undefined";

  const faqs = [
    {
      id: "q1",
      question: t("help.faq1q", "What happens if I clear my browser data?"),
      answer: t(
        "help.faq1a",
        "Your encryption keys are stored in localStorage. Clearing browser data will remove them permanently. Without keys, you cannot decrypt your images. Always export your key bundle first.",
      ),
    },
    {
      id: "q2",
      question: t("help.faq2q", "Can I access my images from another device?"),
      answer: t(
        "help.faq2a",
        "Yes, but you need to import your key bundle on the new device. Sign in with Google, then go to Key Vault and import your previously exported key file.",
      ),
    },
    {
      id: "q3",
      question: t("help.faq3q", "How is my data encrypted?"),
      answer: t(
        "help.faq3a",
        "Each image is encrypted server-side using AES-256-GCM. The unlock key is encrypted with your local key and stored only in your browser. The server never has access to the plaintext unlock key.",
      ),
    },
    {
      id: "q4",
      question: t("help.faq4q", "What does quarantine mean?"),
      answer: t(
        "help.faq4a",
        "Quarantine lists images on the server that have no matching key in your current browser. You can recover them by importing a key bundle or manually entering the unlock key.",
      ),
    },
  ];

  const fixes = [
    {
      icon: <KeyIcon />,
      title: t("help.fixKeys", "Missing Keys"),
      description: t(
        "help.fixKeysDesc",
        "Import a key bundle from another device or backup.",
      ),
      action: routes.keys,
    },
    {
      icon: <StorageIcon />,
      title: t("help.fixStorage", "Storage Full"),
      description: t(
        "help.fixStorageDesc",
        "Delete unused images or empty the trash.",
      ),
      action: routes.trash,
    },
    {
      icon: <SecurityIcon />,
      title: t("help.fixQuarantine", "Quarantined Images"),
      description: t(
        "help.fixQuarantineDesc",
        "Recover images by providing their unlock keys.",
      ),
      action: routes.quarantine,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <HelpIcon color="primary" />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("help.title", "Help & Support")}
        </Typography>
      </Box>
      {/* FAQ */}
      <Typography variant="h6" gutterBottom>
        {t("help.faqTitle", "Frequently Asked Questions")}
      </Typography>
      <Box sx={{ mb: 4 }}>
        {faqs.map((faq) => (
          <Accordion
            key={faq.id}
            expanded={expanded === faq.id}
            onChange={handleAccordion(faq.id)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                sx={{
                  fontWeight: 500,
                }}
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      {/* Storage diagnostics */}
      <Typography variant="h6" gutterBottom>
        {t("help.diagnostics", "Storage Diagnostics")}
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {localStorageAvailable ? (
                <CheckIcon color="success" fontSize="small" />
              ) : (
                <WarningIcon color="error" fontSize="small" />
              )}
              <Typography variant="body2">
                {t("help.localStorage", "localStorage")}
              </Typography>
              <Chip
                label={
                  localStorageAvailable
                    ? t("help.available", "Available")
                    : t("help.unavailable", "Unavailable")
                }
                size="small"
                color={localStorageAvailable ? "success" : "error"}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {cryptoAvailable ? (
                <CheckIcon color="success" fontSize="small" />
              ) : (
                <WarningIcon color="error" fontSize="small" />
              )}
              <Typography variant="body2">
                {t("help.webCrypto", "Web Crypto API")}
              </Typography>
              <Chip
                label={
                  cryptoAvailable
                    ? t("help.available", "Available")
                    : t("help.unavailable", "Unavailable")
                }
                size="small"
                color={cryptoAvailable ? "success" : "error"}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <KeyIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {t("help.storedKeys", "Stored keys: {{count}}", {
                  count: keyCount,
                })}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Guided fixes */}
      <Typography variant="h6" gutterBottom>
        {t("help.guidedFixes", "Guided Fixes")}
      </Typography>
      <Grid container spacing={2}>
        {fixes.map((fix, i) => (
          <Grid size={{ xs: 12, sm: 4 }} key={i}>
            <Card sx={{ height: "100%" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box sx={{ color: "primary.main", mb: 1 }}>{fix.icon}</Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {fix.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    flexGrow: 1,
                    mb: 2,
                  }}
                >
                  {fix.description}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  href={fix.action}
                >
                  {t("help.goFix", "Go")}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
